export type NoteDuration = 'quarter' | 'eighth' | 'dotted-quarter' | 'dotted-eighth' | 'sixteenth'

export type TimeSignature = '4/4' | '6/8'

export interface KeySignature {
  name: string
  abc: string
  mode: 'major' | 'minor'
  scaleSemitones: number[]
}

export interface MelodyNote {
  midi: number
  duration: NoteDuration
}

export interface MelodyConfig {
  tier: number
  timeSignature: TimeSignature
  key: KeySignature
  clef: 'treble' | 'bass'
  measures: number
}

export interface GeneratedMelody {
  config: MelodyConfig
  notes: MelodyNote[]
  abc: string
}

interface MelodyTierConfig {
  tier: number
  intervals: number[]
  durations: NoteDuration[]
  keys: KeySignature[]
  chromaticProbability: number
}

const MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11]
const MINOR_SCALE = [0, 2, 3, 5, 7, 8, 10]

export const KEY_C:  KeySignature = { name: 'C Major',  abc: 'C',   mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_G:  KeySignature = { name: 'G Major',  abc: 'G',   mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_F:  KeySignature = { name: 'F Major',  abc: 'F',   mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_D:  KeySignature = { name: 'D Major',  abc: 'D',   mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_Bb: KeySignature = { name: 'Bb Major', abc: 'Bb',  mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_Eb: KeySignature = { name: 'Eb Major', abc: 'Eb',  mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_A:  KeySignature = { name: 'A Major',  abc: 'A',   mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_Ab: KeySignature = { name: 'Ab Major', abc: 'Ab',  mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_B:  KeySignature = { name: 'B Major',  abc: 'B',   mode: 'major', scaleSemitones: MAJOR_SCALE }
export const KEY_Am: KeySignature = { name: 'A minor',  abc: 'Am',  mode: 'minor', scaleSemitones: MINOR_SCALE }
export const KEY_Dm: KeySignature = { name: 'D minor',  abc: 'Dm',  mode: 'minor', scaleSemitones: MINOR_SCALE }
export const KEY_Em: KeySignature = { name: 'E minor',  abc: 'Em',  mode: 'minor', scaleSemitones: MINOR_SCALE }
export const KEY_Cm: KeySignature = { name: 'C minor',  abc: 'Cm',  mode: 'minor', scaleSemitones: MINOR_SCALE }
export const KEY_Gm: KeySignature = { name: 'G minor',  abc: 'Gm',  mode: 'minor', scaleSemitones: MINOR_SCALE }

const MELODY_TIERS: MelodyTierConfig[] = [
  {
    tier: 1,
    intervals: [1, 2],
    durations: ['quarter'],
    keys: [KEY_C, KEY_G, KEY_F],
    chromaticProbability: 0,
  },
  {
    tier: 2,
    intervals: [1, 2, 3, 4],
    durations: ['quarter', 'eighth'],
    keys: [KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb],
    chromaticProbability: 0,
  },
  {
    tier: 3,
    intervals: [1, 2, 3, 4, 5, 7],
    durations: ['quarter', 'eighth', 'dotted-quarter'],
    keys: [KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb, KEY_Eb, KEY_Am, KEY_Dm, KEY_Em],
    chromaticProbability: 0,
  },
  {
    tier: 4,
    intervals: [1, 2, 3, 4, 5, 7, 8, 9],
    durations: ['quarter', 'eighth', 'dotted-quarter', 'dotted-eighth'],
    keys: [KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb, KEY_Eb, KEY_A, KEY_Ab, KEY_Am, KEY_Dm, KEY_Em, KEY_Cm],
    chromaticProbability: 0.15,
  },
  {
    tier: 5,
    intervals: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    durations: ['quarter', 'eighth', 'dotted-quarter', 'dotted-eighth', 'sixteenth'],
    keys: [KEY_C, KEY_G, KEY_F, KEY_D, KEY_Bb, KEY_Eb, KEY_A, KEY_Ab, KEY_B, KEY_Am, KEY_Dm, KEY_Em, KEY_Cm, KEY_Gm],
    chromaticProbability: 0.25,
  },
]

export function melodyTierForLevel(level: number): MelodyTierConfig {
  const idx = Math.max(0, Math.min(4, Math.round(level) - 1))
  return MELODY_TIERS[idx]
}

const TREBLE_MIN = 60
const TREBLE_MAX = 81
const BASS_MIN = 40
const BASS_MAX = 60

function durationToSixteenths(d: NoteDuration): number {
  switch (d) {
    case 'sixteenth': return 1
    case 'eighth': return 2
    case 'dotted-eighth': return 3
    case 'quarter': return 4
    case 'dotted-quarter': return 6
  }
}

function hasSixteenths(durations: NoteDuration[]): boolean {
  return durations.includes('sixteenth') || durations.includes('dotted-eighth')
}

function measureCapacity(ts: TimeSignature, useSixteenths: boolean): number {
  if (useSixteenths) return ts === '4/4' ? 16 : 12
  return ts === '4/4' ? 8 : 6
}

function durationToUnits(d: NoteDuration, useSixteenths: boolean): number {
  if (useSixteenths) return durationToSixteenths(d)
  switch (d) {
    case 'quarter': return 2
    case 'eighth': return 1
    case 'dotted-quarter': return 3
    default: return durationToSixteenths(d)
  }
}

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function weightedPick<T>(items: T[], weights: number[]): T {
  const total = weights.reduce((a, b) => a + b, 0)
  let r = Math.random() * total
  for (let i = 0; i < items.length; i++) {
    r -= weights[i]
    if (r <= 0) return items[i]
  }
  return items[items.length - 1]
}

function generateRhythm(
  measures: number,
  ts: TimeSignature,
  allowedDurations: NoteDuration[],
): NoteDuration[] {
  const useSixteenths = hasSixteenths(allowedDurations)
  const cap = measureCapacity(ts, useSixteenths)
  const rhythm: NoteDuration[] = []

  for (let m = 0; m < measures; m++) {
    let remaining = cap
    while (remaining > 0) {
      const fitting = allowedDurations.filter(d => durationToUnits(d, useSixteenths) <= remaining)
      if (fitting.length === 0) break

      const weights = fitting.map(d => {
        if (d === 'quarter') return 3
        if (d === 'eighth') return 2
        return 1
      })
      const chosen = weightedPick(fitting, weights)
      rhythm.push(chosen)
      remaining -= durationToUnits(chosen, useSixteenths)
    }
  }

  return rhythm
}

export function getKeyRoot(key: KeySignature): number {
  const roots: Record<string, number> = {
    C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11,
  }
  const letter = key.abc.charAt(0).toUpperCase()
  let root = roots[letter] ?? 0
  if (key.abc.includes('b')) root--
  if (key.abc.includes('#')) root++
  return root
}

function buildScalePitches(key: KeySignature, min: number, max: number): number[] {
  const root = getKeyRoot(key)
  const pitches: number[] = []
  for (let midi = min; midi <= max; midi++) {
    const pc = ((midi % 12) - root + 12) % 12
    if (key.scaleSemitones.includes(pc)) {
      pitches.push(midi)
    }
  }
  return pitches
}

function isScaleTone(midi: number, key: KeySignature): boolean {
  const root = getKeyRoot(key)
  const pc = ((midi % 12) - root + 12) % 12
  return key.scaleSemitones.includes(pc)
}

function isTonicTriad(midi: number, key: KeySignature): boolean {
  const root = getKeyRoot(key)
  const pc = ((midi % 12) - root + 12) % 12
  if (key.mode === 'major') return [0, 4, 7].includes(pc)
  return [0, 3, 7].includes(pc)
}

function nearestScaleTone(midi: number, key: KeySignature, min: number, max: number): number {
  for (let offset = 0; offset <= 12; offset++) {
    if (midi + offset <= max && isScaleTone(midi + offset, key)) return midi + offset
    if (midi - offset >= min && isScaleTone(midi - offset, key)) return midi - offset
  }
  return midi
}

function generatePitches(
  noteCount: number,
  key: KeySignature,
  clef: 'treble' | 'bass',
  tierConfig: MelodyTierConfig,
): number[] {
  const min = clef === 'treble' ? TREBLE_MIN : BASS_MIN
  const max = clef === 'treble' ? TREBLE_MAX : BASS_MAX

  const scalePitches = buildScalePitches(key, min, max)
  const tonicTriadPitches = scalePitches.filter(p => isTonicTriad(p, key))

  const mid = Math.floor((min + max) / 2)
  const startCandidates = tonicTriadPitches.filter(p => Math.abs(p - mid) <= 7)
  const startPitch = startCandidates.length > 0 ? pickRandom(startCandidates) : pickRandom(tonicTriadPitches)

  const pitches: number[] = [startPitch]
  let lastDirection = 0

  for (let i = 1; i < noteCount; i++) {
    const prev = pitches[i - 1]
    const isLast = i === noteCount - 1
    const prevInterval = i >= 2 ? Math.abs(pitches[i - 1] - pitches[i - 2]) : 0
    const needStepBack = prevInterval >= 5

    let direction: number
    if (needStepBack) {
      direction = pitches[i - 1] > pitches[i - 2] ? -1 : 1
    } else if (i >= 2 && lastDirection !== 0) {
      direction = Math.random() < 0.7 ? -lastDirection : lastDirection
    } else {
      direction = Math.random() < 0.5 ? 1 : -1
    }

    if (prev >= max - 2) direction = -1
    if (prev <= min + 2) direction = 1

    const useChromatic = !isLast && Math.random() < tierConfig.chromaticProbability

    const intervalPool = needStepBack
      ? [1, 2]
      : tierConfig.intervals

    const intervalWeights = intervalPool.map(iv => {
      if (iv <= 2) return 3
      if (iv <= 4) return 2
      return 1
    })

    let candidate: number
    let attempts = 0
    do {
      const interval = weightedPick(intervalPool, intervalWeights)
      candidate = prev + direction * interval

      if (candidate < min || candidate > max) {
        direction = -direction
        candidate = prev + direction * interval
      }
      attempts++
    } while ((candidate < min || candidate > max) && attempts < 20)

    candidate = Math.max(min, Math.min(max, candidate))

    if (!useChromatic) {
      candidate = nearestScaleTone(candidate, key, min, max)
    }

    if (isLast) {
      const nearest = tonicTriadPitches.reduce((best, p) =>
        Math.abs(p - candidate) < Math.abs(best - candidate) ? p : best
      )
      if (Math.abs(nearest - candidate) <= 4) {
        candidate = nearest
      }
    }

    lastDirection = candidate > prev ? 1 : candidate < prev ? -1 : lastDirection
    pitches.push(candidate)
  }

  return pitches
}

const ABC_NOTES = ['C', '^C', 'D', '^D', 'E', 'F', '^F', 'G', '^G', 'A', '^A', 'B']
const ABC_FLAT_NOTES = ['C', '_D', 'D', '_E', 'E', 'F', '_G', 'G', '_A', 'A', '_B', 'B']

export function midiToAbcNote(midi: number, key: KeySignature): string {
  const octave = Math.floor(midi / 12) - 1
  const noteIdx = midi % 12

  const root = getKeyRoot(key)
  const pc = ((noteIdx - root + 12) % 12)
  const usesFlats = key.abc.includes('b') || ['F', 'Dm', 'Gm', 'Cm'].includes(key.abc)
  const isInScale = key.scaleSemitones.includes(pc)

  let baseLetter: string
  if (isInScale) {
    if (usesFlats) {
      // In flat keys, Bb=B, Eb=E, Ab=A, Db=D, Gb=G — key sig handles the flat
      const flatScaleNames = ['C', 'D', 'D', 'E', 'E', 'F', 'G', 'G', 'A', 'A', 'B', 'B']
      baseLetter = flatScaleNames[noteIdx]
    } else {
      // In sharp keys, F#=F, C#=C, G#=G — key sig handles the sharp
      const sharpScaleNames = ['C', 'C', 'D', 'D', 'E', 'F', 'F', 'G', 'G', 'A', 'A', 'B']
      baseLetter = sharpScaleNames[noteIdx]
    }
  } else {
    baseLetter = usesFlats ? ABC_FLAT_NOTES[noteIdx] : ABC_NOTES[noteIdx]
  }

  if (octave >= 5) {
    const ticks = octave - 5
    return baseLetter.replace(/^([_^]?)([A-G])/, (_, acc, n) => acc + n.toLowerCase()) + "'".repeat(ticks)
  } else if (octave === 4) {
    return baseLetter
  } else {
    const commas = 4 - octave
    return baseLetter + ','.repeat(commas)
  }
}

function durationToAbcSuffix(d: NoteDuration, useSixteenths: boolean): string {
  if (useSixteenths) {
    switch (d) {
      case 'sixteenth': return ''
      case 'dotted-eighth': return '3/2'
      case 'eighth': return '2'
      case 'quarter': return '4'
      case 'dotted-quarter': return '6'
    }
  }
  switch (d) {
    case 'eighth': return ''
    case 'quarter': return '2'
    case 'dotted-quarter': return '3'
    default: return ''
  }
}

export function melodyToAbc(melody: GeneratedMelody): string {
  const { config, notes } = melody
  const useSixteenths = hasSixteenths(
    MELODY_TIERS[config.tier - 1]?.durations ?? ['quarter']
  )

  const defaultLength = useSixteenths ? '1/16' : '1/8'
  const clefAbc = config.clef === 'bass' ? 'bass' : 'treble'

  let header = `X:1\n`
  header += `M:${config.timeSignature}\n`
  header += `L:${defaultLength}\n`
  header += `Q:1/4=80\n`
  header += `K:${config.key.abc} clef=${clefAbc}\n`

  const cap = measureCapacity(config.timeSignature, useSixteenths)
  let units = 0
  let body = ''

  for (const note of notes) {
    const abcNote = midiToAbcNote(note.midi, config.key)
    const suffix = durationToAbcSuffix(note.duration, useSixteenths)
    body += abcNote + suffix
    units += durationToUnits(note.duration, useSixteenths)

    if (units >= cap) {
      body += '|'
      units = 0
    }
  }

  if (!body.endsWith('|')) body += '|'

  return header + body + '\n'
}

export function inputNotesToAbc(
  notes: MelodyNote[],
  config: MelodyConfig,
): string {
  const tierConfig = MELODY_TIERS[config.tier - 1] ?? MELODY_TIERS[0]
  const useSixteenths = hasSixteenths(tierConfig.durations)
  const defaultLength = useSixteenths ? '1/16' : '1/8'
  const clefAbc = config.clef === 'bass' ? 'bass' : 'treble'

  let header = `X:1\n`
  header += `M:${config.timeSignature}\n`
  header += `L:${defaultLength}\n`
  header += `K:${config.key.abc} clef=${clefAbc}\n`

  const cap = measureCapacity(config.timeSignature, useSixteenths)
  const totalCap = cap * config.measures
  let units = 0
  let body = ''

  for (const note of notes) {
    body += midiToAbcNote(note.midi, config.key) + durationToAbcSuffix(note.duration, useSixteenths)
    units += durationToUnits(note.duration, useSixteenths)
    if (units % cap === 0 && units < totalCap) body += '|'
  }

  // Pad remaining with invisible rests
  if (units < totalCap) {
    const inMeasure = units % cap
    if (inMeasure > 0) {
      body += `x${cap - inMeasure}|`
      units += cap - inMeasure
    }
    while (units < totalCap) {
      body += `x${cap}|`
      units += cap
    }
  }

  return header + body + '\n'
}

export function buildGradedAbc(
  studentNotes: MelodyNote[],
  expectedNotes: MelodyNote[],
  config: MelodyConfig,
  gradeResult: GradeResult,
): { abc: string; greenIndices: Set<number>; redIndices: Set<number>; correctNoteIndices: Set<number> } {
  const tierConfig = MELODY_TIERS[config.tier - 1] ?? MELODY_TIERS[0]
  const useSixteenths = hasSixteenths(tierConfig.durations)
  const defaultLength = useSixteenths ? '1/16' : '1/8'
  const clefAbc = config.clef === 'bass' ? 'bass' : 'treble'

  let header = `X:1\n`
  header += `M:${config.timeSignature}\n`
  header += `L:${defaultLength}\n`
  header += `K:${config.key.abc} clef=${clefAbc}\n`

  const cap = measureCapacity(config.timeSignature, useSixteenths)
  let units = 0
  let body = ''
  let noteIdx = 0

  const greenIndices = new Set<number>()
  const redIndices = new Set<number>()
  const correctNoteIndices = new Set<number>()

  // First note is always given/correct
  const firstNote = studentNotes[0] ?? expectedNotes[0]
  body += midiToAbcNote(firstNote.midi, config.key) + durationToAbcSuffix(firstNote.duration, useSixteenths)
  greenIndices.add(0)
  units += durationToUnits(firstNote.duration, useSixteenths)
  if (units >= cap) { body += '|'; units = 0 }
  noteIdx = 1

  for (let i = 0; i < gradeResult.noteResults.length; i++) {
    const result = gradeResult.noteResults[i]

    if (result.fullyCorrect) {
      const note = studentNotes[i + 1]
      body += midiToAbcNote(note.midi, config.key) + durationToAbcSuffix(note.duration, useSixteenths)
      greenIndices.add(noteIdx)
      units += durationToUnits(note.duration, useSixteenths)
      noteIdx++
    } else if (result.actualMidi !== null) {
      const studentNote = studentNotes[i + 1]
      body += midiToAbcNote(studentNote.midi, config.key) + durationToAbcSuffix(studentNote.duration, useSixteenths)
      redIndices.add(noteIdx)
      units += durationToUnits(studentNote.duration, useSixteenths)
      noteIdx++
    } else {
      // Missing note: show expected note in red (they didn't enter it)
      const expected = expectedNotes[i + 1]
      if (expected) {
        body += midiToAbcNote(expected.midi, config.key) + durationToAbcSuffix(expected.duration, useSixteenths)
        redIndices.add(noteIdx)
        units += durationToUnits(expected.duration, useSixteenths)
        noteIdx++
      }
    }

    if (units >= cap) { body += '|'; units = 0 }
  }

  // Pad remaining
  const totalCap = cap * config.measures
  while (units > 0 && units < totalCap) {
    const remaining = cap - (units % cap)
    body += `x${remaining}|`
    units += remaining
  }

  return { abc: header + body + '\n', greenIndices, redIndices, correctNoteIndices }
}

export interface GradeResult {
  noteResults: NoteGrade[]
  correctCount: number
  totalCount: number
  score: number
}

export interface NoteGrade {
  expectedMidi: number
  expectedDuration: NoteDuration
  actualMidi: number | null
  actualDuration: NoteDuration | null
  pitchCorrect: boolean
  durationCorrect: boolean
  fullyCorrect: boolean
}

export function gradeMelody(expected: MelodyNote[], actual: MelodyNote[]): GradeResult {
  const compareNotes = expected.slice(1)
  const studentNotes = actual.slice(1)
  const totalCount = compareNotes.length

  const noteResults: NoteGrade[] = compareNotes.map((exp, i) => {
    const act = studentNotes[i] ?? null
    const pitchCorrect = act !== null && act.midi === exp.midi
    const durationCorrect = act !== null && act.duration === exp.duration
    return {
      expectedMidi: exp.midi,
      expectedDuration: exp.duration,
      actualMidi: act?.midi ?? null,
      actualDuration: act?.duration ?? null,
      pitchCorrect,
      durationCorrect,
      fullyCorrect: pitchCorrect && durationCorrect,
    }
  })

  const correctCount = noteResults.filter(r => r.fullyCorrect).length
  return {
    noteResults,
    correctCount,
    totalCount,
    score: totalCount > 0 ? correctCount / totalCount : 0,
  }
}

export function generateMelody(
  level: number,
  clefPreference: 'TREBLE' | 'BASS' | 'BOTH',
): GeneratedMelody {
  const tierConfig = melodyTierForLevel(level)

  const clef: 'treble' | 'bass' =
    clefPreference === 'BOTH'
      ? pickRandom(['treble', 'bass'])
      : clefPreference === 'BASS'
        ? 'bass'
        : 'treble'

  const key = pickRandom(tierConfig.keys)

  let timeSignature: TimeSignature = '4/4'
  if (tierConfig.tier >= 3 && Math.random() < 0.3) {
    timeSignature = '6/8'
  }

  const measures = 4

  const config: MelodyConfig = { tier: tierConfig.tier, timeSignature, key, clef, measures }

  const rhythm = generateRhythm(measures, timeSignature, tierConfig.durations)
  const pitches = generatePitches(rhythm.length, key, clef, tierConfig)

  const notes: MelodyNote[] = rhythm.map((duration, i) => ({
    midi: pitches[i],
    duration,
  }))

  const melody: GeneratedMelody = { config, notes, abc: '' }
  melody.abc = melodyToAbc(melody)

  return melody
}

export function midiToNoteName(midi: number): string {
  const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
  const octave = Math.floor(midi / 12) - 1
  return noteNames[midi % 12] + octave
}

export function noteNameToMidi(name: string, accidental: 'natural' | 'sharp' | 'flat', octave: number): number {
  const baseMap: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }
  let midi = baseMap[name] + (octave + 1) * 12
  if (accidental === 'sharp') midi++
  if (accidental === 'flat') midi--
  return midi
}

export function inferOctave(
  pitchClass: string,
  accidental: 'natural' | 'sharp' | 'flat',
  prevMidi: number,
  clef: 'treble' | 'bass',
): number {
  const min = clef === 'treble' ? TREBLE_MIN : BASS_MIN
  const max = clef === 'treble' ? TREBLE_MAX : BASS_MAX
  const minOctave = Math.floor(min / 12) - 1
  const maxOctave = Math.floor(max / 12) - 1

  let bestOctave = minOctave
  let bestDist = Infinity

  for (let oct = minOctave; oct <= maxOctave + 1; oct++) {
    const midi = noteNameToMidi(pitchClass, accidental, oct)
    if (midi < min || midi > max) continue
    const dist = Math.abs(midi - prevMidi)
    if (dist < bestDist) {
      bestDist = dist
      bestOctave = oct
    }
  }

  return bestOctave
}

export const DURATION_SYMBOLS: Record<NoteDuration, string> = {
  'sixteenth': '𝅘𝅥𝅱',
  'dotted-eighth': '𝅘𝅥𝅮.',
  'eighth': '𝅘𝅥𝅮',
  'quarter': '𝅘𝅥',
  'dotted-quarter': '𝅘𝅥.',
}
